import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PaymentTerms } from "@/types/general";

export type PaymentTermTypeOption = {
    key: PaymentTerms.Type;
    title: string;
};

/**
 * Single source of truth for the payment term type labels, shared by the terms table and the mutation modal.
 *
 * The labels map is exhaustive over PaymentTerms.Type, so introducing a new type on the backend
 * breaks the compilation here instead of silently mislabelling the new type in the UI.
 */
export function usePaymentTermTypeOptions() {
    const { t } = useTranslation("", { keyPrefix: "settings.general.paymentTerms.paymentTypes" });

    const labels = useMemo<Record<PaymentTerms.Type, string>>(
        () => ({
            0: t("daysAfter"),
            1: t("prepayment"),
            2: t("endOfMonth"),
        }),
        [t]
    );

    const options = useMemo<PaymentTermTypeOption[]>(
        () =>
            (Object.entries(labels) as [`${PaymentTerms.Type}`, string][]).map(([key, title]) => ({
                key: Number(key) as PaymentTerms.Type,
                title,
            })),
        [labels]
    );

    const getLabel = useCallback((type: PaymentTerms.Type) => labels[type], [labels]);

    return { options, getLabel };
}
